import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Award, CheckCircle2, ArrowRight, Sparkles, ShieldCheck, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import SalesChatWidget from "../components/SalesChatWidget";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/plans");
      setPlans(data);
    })();
  }, []);

  const knownCoupons = { LAUNCH50: 0.5, EARLY25: 0.25, PARTNER10: 0.1 };
  const applyCoupon = () => {
    const code = coupon.trim().toUpperCase();
    if (!code) return;
    if (knownCoupons[code] != null) {
      setCouponApplied({ code, discount: knownCoupons[code] });
      setError("");
    } else {
      setCouponApplied(null);
      setError(`Coupon "${code}" is not valid.`);
    }
  };

  const onCheckout = async (planId) => {
    setError("");
    if (!user) {
      navigate("/login?next=/pricing");
      return;
    }
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
        coupon_code: couponApplied?.code || undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-950" data-testid="pricing-page">
      {/* Top nav */}
      <nav className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-orange-600 grid place-items-center rounded-sm">
              <Award className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black tracking-tight text-lg">
              PLATINUM<span className="text-orange-600">1</span>
            </span>
          </Link>
          <Link to={user ? "/dashboard" : "/login"} data-testid="back-link" className="text-sm font-semibold text-slate-700 hover:text-orange-600 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {user ? "Back to dashboard" : "Sign in"}
          </Link>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-[0.2em] text-slate-700">
            <Sparkles className="w-3 h-3 text-orange-600" /> Pick your plan
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
            Get <span className="text-orange-600">Platinum 1 Certified</span>.
          </h1>
          <p className="mt-5 text-lg text-slate-600">
            One-time payment. Lifetime access. <strong className="text-slate-900">3-day money-back guarantee</strong> — no questions.
          </p>
        </div>

        {/* Coupon */}
        <div className="mt-10 max-w-md mx-auto">
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2 text-center">
            Have a coupon?
          </label>
          <div className="flex gap-2">
            <input
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              data-testid="coupon-input"
              placeholder="LAUNCH50"
              className="flex-1 bg-white border border-slate-300 rounded-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-base p-3 outline-none uppercase tracking-wider"
            />
            <button
              onClick={applyCoupon}
              data-testid="apply-coupon-btn"
              className="bg-slate-950 hover:bg-slate-800 text-white px-5 rounded-sm font-semibold text-sm"
            >
              Apply
            </button>
          </div>
          {couponApplied && (
            <div data-testid="coupon-applied" className="mt-3 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> <strong>{couponApplied.code}</strong> applied — {Math.round(couponApplied.discount * 100)}% off.
            </div>
          )}
          {error && (
            <div data-testid="pricing-error" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}
        </div>

        {/* Plans */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p) => {
            const original = p.price;
            const final = couponApplied ? +(original * (1 - couponApplied.discount)).toFixed(2) : original;
            const recommended = !!p.recommended;
            return (
              <div
                key={p.plan_id}
                data-testid={`plan-card-${p.plan_id}`}
                className={`border rounded-sm p-7 bg-white flex flex-col ${recommended ? "border-orange-600 shadow-lg relative" : "border-slate-200"}`}
              >
                {recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-sm">
                    Most popular
                  </div>
                )}
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
                  {p.name}
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  {couponApplied && (
                    <span className="text-slate-400 line-through text-2xl">${original.toFixed(0)}</span>
                  )}
                  <span className="font-display text-5xl font-black tracking-tighter text-slate-950">
                    ${final.toFixed(final % 1 === 0 ? 0 : 2)}
                  </span>
                  <span className="text-slate-500 text-sm">one-time</span>
                </div>
                <p className="text-slate-600 mt-3 text-sm">{p.tagline}</p>
                <ul className="mt-6 space-y-2 text-sm text-slate-800 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onCheckout(p.plan_id)}
                  disabled={loadingPlan === p.plan_id}
                  data-testid={`buy-${p.plan_id}-btn`}
                  className={`mt-7 py-3 rounded-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                    recommended
                      ? "bg-orange-600 hover:bg-orange-700 text-white"
                      : "bg-slate-950 hover:bg-slate-800 text-white"
                  } disabled:opacity-60`}
                >
                  {loadingPlan === p.plan_id ? "Redirecting…" : (
                    <>
                      {user?.plan === p.plan_id ? "Current plan" : `Get ${p.name}`} <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Guarantees */}
        <div className="mt-16 max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-slate-700">
          <div className="border border-slate-200 rounded-sm p-5 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-slate-900">3-day refund</div>
              <div className="text-slate-600 text-xs mt-1">Not the right fit? Email us within 3 days for a full refund.</div>
            </div>
          </div>
          <div className="border border-slate-200 rounded-sm p-5 flex items-start gap-3">
            <Award className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-slate-900">Lifetime access</div>
              <div className="text-slate-600 text-xs mt-1">Buy once, study at your pace. No expiring seats.</div>
            </div>
          </div>
          <div className="border border-slate-200 rounded-sm p-5 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-slate-900">Secure checkout</div>
              <div className="text-slate-600 text-xs mt-1">Powered by Stripe. We never see your card details.</div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-12 max-w-xl mx-auto">
          Free preview includes the first 2 modules (Safety & Tool Usage). Certification quizzes and the
          downloadable PDF credential require any paid plan.
        </p>
      </section>
      <SalesChatWidget />
    </div>
  );
}
